import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import DraftList from './components/DraftList';
import DraftDetail from './components/DraftDetail';
import CreateDraftForm from './components/CreateDraftForm';
import Login from './authComponents/Login';
import Signup from './authComponents/Signup';
import PasswordReset from './authComponents/PasswordReset';
import ChangePassword from './authComponents/ChangePassword';
import { clearAutoRefresh, setupAutoRefresh } from './utils/tokenRefresh';

interface StoredUser {
    username: string;
    email?: string;
}

const getStoredUser = (): StoredUser | null => {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    try {
        return JSON.parse(raw) as StoredUser;
    } catch (error) {
        return null;
    }
};

const App: React.FC = () => {
    const location = useLocation();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
        Boolean(localStorage.getItem('access_token'))
    );
    const [user, setUser] = useState<StoredUser | null>(() => getStoredUser());

    useEffect(() => {
        if (isAuthenticated) {
            setupAutoRefresh();
        } else {
            clearAutoRefresh();
        }
    }, [isAuthenticated]);

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        setUser(null);
    };

    const handleAuthSuccess = () => {
        setIsAuthenticated(true);
        setUser(getStoredUser());
    };

    const headerSubtitle = useMemo(() => {
        if (!user?.username) return 'Serpentine drafts in one shared board.';
        return `Signed in as ${user.username}`;
    }, [user]);

    const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
        if (!isAuthenticated) {
            return <Navigate to="/login" state={{ from: location.pathname }} replace />;
        }
        return children;
    };

    return (
        <div className="app-shell">
            <header className="app-header">
                <div className="brand">
                    <Link to="/" className="brand-link">Drafts</Link>
                </div>
                <div className="header-actions">
                    <div className="header-subtitle">{headerSubtitle}</div>
                    {isAuthenticated ? (
                        <>
                            <Link to="/change-password" className="btn-secondary">Change password</Link>
                            <button className="btn-secondary" onClick={handleLogout}>Log out</button>
                        </>
                    ) : (
                        <div className="auth-links">
                            <Link to="/login" className="btn-secondary">Log in</Link>
                            <Link to="/signup" className="btn-primary">Sign up</Link>
                        </div>
                    )}
                </div>
            </header>

            <main className="app-main">
                <Routes>
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <DraftList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/drafts"
                        element={
                            <ProtectedRoute>
                                <DraftList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/drafts/new"
                        element={
                            <ProtectedRoute>
                                <CreateDraftForm />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/drafts/:draftId"
                        element={
                            <ProtectedRoute>
                                <DraftDetail />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/login"
                        element={
                            isAuthenticated
                                ? <Navigate to="/" replace />
                                : <Login onLoginSuccess={handleAuthSuccess} />
                        }
                    />
                    <Route
                        path="/signup"
                        element={
                            isAuthenticated
                                ? <Navigate to="/" replace />
                                : <Signup onSignupSuccess={handleAuthSuccess} />
                        }
                    />
                    <Route
                        path="/change-password"
                        element={
                            <ProtectedRoute>
                                <ChangePassword />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reset-password"
                        element={<PasswordReset />}
                    />
                    <Route
                        path="*"
                        element={
                            <div className="empty-state">
                                <h2>Page not found</h2>
                                <p>That route is not part of this draft yet.</p>
                                <Link to="/" className="btn-primary">Back to dashboard</Link>
                            </div>
                        }
                    />
                </Routes>
            </main>
        </div>
    );
};

export default App;
